import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db, ADMIN_EMAIL } from '../firebase';
import Conversaciones from './Conversaciones';
import {
  ShieldCheck,
  Loader2,
  Save,
  LogOut,
  Inbox,
  Send,
  Check,
  FileText,
  Receipt,
  X,
  MessagesSquare,
} from 'lucide-react';

const fmtFecha = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return '';
  return d.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [autorizado, setAutorizado] = useState(false);
  const [verificando, setVerificando] = useState(true);

  // --- Contexto ---
  const [contexto, setContexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  // --- Preguntas sin respuesta ---
  const [pendientes, setPendientes] = useState([]);
  const [respuestas, setRespuestas] = useState({}); // { [id]: texto }
  const [enviando, setEnviando] = useState({}); // { [id]: bool }

  // --- Comprobantes de pago ---
  const [comprobantes, setComprobantes] = useState([]);
  const [procesandoComp, setProcesandoComp] = useState({}); // { [id]: bool }

  // --- Navegación a una conversación (desde un comprobante) ---
  const [convSeleccionada, setConvSeleccionada] = useState(null);
  const convSectionRef = useRef(null);

  const irAConversacion = (phone) => {
    setConvSeleccionada(phone);
    convSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Guard de autorización.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u || u.email !== ADMIN_EMAIL) {
        navigate('/login', { replace: true });
        return;
      }
      setAutorizado(true);
      setVerificando(false);
      // Cargar contexto actual.
      const snap = await getDoc(doc(db, 'chat_config', 'main'));
      setContexto(snap.exists() ? snap.data().context || '' : '');
    });
    return () => unsub();
  }, [navigate]);

  // Escuchar preguntas pendientes en tiempo real.
  useEffect(() => {
    if (!autorizado) return;
    // Solo filtro de igualdad (sin orderBy) para no requerir un índice
    // compuesto en Firestore. Ordenamos en el cliente por fecha.
    const q = query(
      collection(db, 'chat_unanswered'),
      where('status', '==', 'pendiente')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta; // más recientes primero
        });
        setPendientes(docs);
      },
      (e) => console.error('Error escuchando pendientes:', e)
    );
    return () => unsub();
  }, [autorizado]);

  // Escuchar comprobantes de pago en tiempo real.
  useEffect(() => {
    if (!autorizado) return;
    const unsub = onSnapshot(
      collection(db, 'chat_comprobantes'),
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
        setComprobantes(docs);
      },
      (e) => console.error('Error escuchando comprobantes:', e)
    );
    return () => unsub();
  }, [autorizado]);

  const resolverComprobante = async (comp, aprobado) => {
    setProcesandoComp((p) => ({ ...p, [comp.id]: true }));
    try {
      // 1. Actualizar el estado del comprobante.
      await updateDoc(doc(db, 'chat_comprobantes', comp.id), {
        status: aprobado ? 'verificado' : 'rechazado',
        resolvedAt: serverTimestamp(),
      });
      // 2. Avisar al cliente en su conversación.
      await addDoc(
        collection(db, 'chat_conversations', comp.phone, 'messages'),
        {
          role: 'assistant',
          tipo: 'admin',
          text: aprobado
            ? '✅ ¡Confirmamos tu pago! En breve te contactamos por aquí para acordar los detalles de tu retrato.'
            : 'No pudimos validar tu comprobante de pago. ¿Podrías reenviarlo o verificar los datos? Gracias.',
          createdAt: serverTimestamp(),
        }
      );
      await setDoc(
        doc(db, 'chat_conversations', comp.phone),
        { updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error('Error resolviendo comprobante:', e);
      alert('No se pudo actualizar el comprobante.');
    } finally {
      setProcesandoComp((p) => ({ ...p, [comp.id]: false }));
    }
  };

  const guardarContexto = async () => {
    setGuardando(true);
    setGuardado(false);
    try {
      await setDoc(
        doc(db, 'chat_config', 'main'),
        { context: contexto, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2500);
    } catch (e) {
      console.error('Error guardando contexto:', e);
      alert('No se pudo guardar el contexto.');
    } finally {
      setGuardando(false);
    }
  };

  const responder = async (pregunta) => {
    const texto = (respuestas[pregunta.id] || '').trim();
    if (!texto) return;
    setEnviando((p) => ({ ...p, [pregunta.id]: true }));
    try {
      // 1. Publicar la respuesta en la conversación del usuario.
      await addDoc(
        collection(db, 'chat_conversations', pregunta.phone, 'messages'),
        {
          role: 'assistant',
          text: texto,
          tipo: 'admin',
          createdAt: serverTimestamp(),
          enRespuestaA: pregunta.question,
        }
      );
      // 2. Marcar la pregunta como respondida.
      await updateDoc(doc(db, 'chat_unanswered', pregunta.id), {
        status: 'respondida',
        answer: texto,
        answeredAt: serverTimestamp(),
      });

      // 3. Anexar la pregunta+respuesta al contexto para que la IA sepa
      //    responderla la próxima vez (sección gestionada automáticamente).
      const HEADER = '=== RESPUESTAS APRENDIDAS (agregadas desde el panel) ===';
      const entrada = `P: ${pregunta.question}\nR: ${texto}`;
      const base = contexto || '';
      const nuevoContexto = base.includes(HEADER)
        ? `${base.trimEnd()}\n\n${entrada}`
        : `${base.trimEnd()}\n\n${HEADER}\n\n${entrada}`;
      await setDoc(
        doc(db, 'chat_config', 'main'),
        { context: nuevoContexto, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setContexto(nuevoContexto);

      setRespuestas((p) => {
        const n = { ...p };
        delete n[pregunta.id];
        return n;
      });
    } catch (e) {
      console.error('Error respondiendo:', e);
      alert('No se pudo enviar la respuesta.');
    } finally {
      setEnviando((p) => ({ ...p, [pregunta.id]: false }));
    }
  };

  const tarjetaComprobante = (c) => (
    <div key={c.id} className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-3">
      <button
        onClick={() => irAConversacion(c.phone)}
        title="Ver la conversación de este cliente"
        className="group relative block w-full"
      >
        <img
          src={c.imageUrl}
          alt="comprobante"
          loading="lazy"
          className="w-full h-44 object-cover rounded-lg border border-white/10 mb-2"
        />
        <span className="absolute inset-0 mb-2 rounded-lg bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="flex items-center gap-1.5 text-white text-xs font-bold bg-blue-600/90 px-3 py-1.5 rounded-lg">
            <MessagesSquare size={14} /> Ver conversación
          </span>
        </span>
      </button>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-mono text-slate-500 truncate max-w-[55%]">
          {c.phone}
        </span>
        <span className="text-[11px] text-slate-500">{fmtFecha(c.createdAt)}</span>
      </div>
      {c.status === 'pendiente' ? (
        <div className="flex gap-2">
          <button
            onClick={() => resolverComprobante(c, true)}
            disabled={procesandoComp[c.id]}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1.5"
          >
            {procesandoComp[c.id] ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
            Verificar
          </button>
          <button
            onClick={() => resolverComprobante(c, false)}
            disabled={procesandoComp[c.id]}
            className="flex-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-1.5"
          >
            <X size={14} /> Rechazar
          </button>
        </div>
      ) : (
        <span
          className={`block text-center text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-lg border ${
            c.status === 'verificado'
              ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
              : 'bg-red-500/15 text-red-300 border-red-500/40'
          }`}
        >
          {c.status}
        </span>
      )}
    </div>
  );

  if (verificando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  const compPendientes = comprobantes.filter((c) => c.status === 'pendiente');
  const compResueltos = comprobantes.filter((c) => c.status !== 'pendiente');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-emerald-400" size={20} />
          <span className="font-bold text-sm">Panel del administrador</span>
        </div>
        <button
          onClick={() => signOut(auth).then(() => navigate('/login'))}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-400 flex items-center gap-2 text-xs font-bold px-3"
        >
          <LogOut size={16} /> Salir
        </button>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-6">
        {/* Contexto */}
        <section className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={18} className="text-blue-400" />
            <h2 className="font-bold">Contexto del asistente</h2>
          </div>
          <p className="text-slate-400 text-xs mb-3">
            Toda la información con la que la IA puede responder. Si algo no está aquí, el
            asistente dirá que no cuenta con la información y guardará la pregunta abajo.
          </p>
          <textarea
            value={contexto}
            onChange={(e) => setContexto(e.target.value)}
            rows={12}
            placeholder="Ej: Rocharamas vende retratos en hilo. Horario: lun-vie 9-18h. Precios: ..."
            className="w-full bg-slate-900 border border-slate-700 rounded-xl text-slate-100 text-sm p-4 focus:outline-none focus:border-blue-500 resize-y font-mono leading-relaxed"
          />
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={guardarContexto}
              disabled={guardando}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 transition-colors"
            >
              {guardando ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Guardar contexto
            </button>
            {guardado && (
              <span className="text-emerald-400 text-sm flex items-center gap-1">
                <Check size={16} /> Guardado
              </span>
            )}
          </div>
        </section>

        {/* Preguntas sin respuesta */}
        <section className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Inbox size={18} className="text-amber-400" />
            <h2 className="font-bold">Preguntas sin respuesta</h2>
            {pendientes.length > 0 && (
              <span className="ml-1 text-[11px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5">
                {pendientes.length}
              </span>
            )}
          </div>
          <p className="text-slate-400 text-xs mb-4">
            Cuando respondas, el mensaje aparecerá en la conversación del cliente.
          </p>

          {pendientes.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-8">
              <Check size={32} className="mx-auto mb-2 text-slate-700" />
              No hay preguntas pendientes. 🎉
            </div>
          ) : (
            <div className="space-y-4">
              {pendientes.map((p) => (
                <div key={p.id} className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-mono text-slate-500">{p.phone}</span>
                  </div>
                  <p className="text-slate-100 text-sm mb-3 font-medium">“{p.question}”</p>
                  <textarea
                    value={respuestas[p.id] || ''}
                    onChange={(e) => setRespuestas((r) => ({ ...r, [p.id]: e.target.value }))}
                    rows={3}
                    placeholder="Escribe la respuesta para el cliente…"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-sm p-3 focus:outline-none focus:border-emerald-500 resize-y"
                  />
                  <button
                    onClick={() => responder(p)}
                    disabled={enviando[p.id] || !(respuestas[p.id] || '').trim()}
                    className="mt-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 text-sm transition-colors"
                  >
                    {enviando[p.id] ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
                    Enviar respuesta
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Comprobantes de pago */}
        <section className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Receipt size={18} className="text-emerald-400" />
            <h2 className="font-bold">Comprobantes de pago</h2>
            {comprobantes.some((c) => c.status === 'pendiente') && (
              <span className="ml-1 text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5">
                {comprobantes.filter((c) => c.status === 'pendiente').length} por verificar
              </span>
            )}
          </div>
          <p className="text-slate-400 text-xs mb-4">
            Imágenes que los clientes enviaron como comprobante. Verifícalas y el
            cliente recibirá el aviso en su chat.
          </p>

          {comprobantes.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-8">
              <Receipt size={32} className="mx-auto mb-2 text-slate-700" />
              Aún no hay comprobantes.
            </div>
          ) : (
            <div className="space-y-5">
              {/* Por verificar */}
              {compPendientes.length > 0 && (
                <div>
                  <div className="text-amber-300 text-xs font-bold uppercase tracking-wider mb-2">
                    Por verificar ({compPendientes.length})
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {compPendientes.map(tarjetaComprobante)}
                  </div>
                </div>
              )}

              {/* Historial (verificados / rechazados) */}
              {compResueltos.length > 0 && (
                <div>
                  <div className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">
                    Historial ({compResueltos.length})
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {compResueltos.map(tarjetaComprobante)}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Conversaciones */}
        <div ref={convSectionRef} className="scroll-mt-20">
          <Conversaciones
            selPhone={convSeleccionada}
            onSelPhone={setConvSeleccionada}
          />
        </div>
      </main>
    </div>
  );
}
